import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BookOpen, Users, GraduationCap, ChevronRight, Search, ClipboardCheck } from "lucide-react";
import apiClient from "../../utils/apiClient";
import { useBranch } from "../../contexts/BranchContext";
import { usePlan } from "../../contexts/PlanContext";
import { PageHeader, EmptyState } from "../ui";
import Shimmer from "../Common/Shimmer";
import BatchAttendanceRegister from "./BatchAttendanceRegister";
import "./attendance.scss";

/**
 * BatchAttendanceBrowserPage — the "Batch Register" entry point.
 *
 * Route /attendance/register            → grid of active batches (pick one).
 * Route /attendance/register/:batchId   → that batch's attendance book
 *                                          (BatchAttendanceRegister, week view).
 *
 * Gated by the `attendanceManagement` plan feature (the register endpoint is
 * gated server-side too).
 */

const DOW_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const BatchAttendanceBrowserPage = () => {
  const navigate = useNavigate();
  const { batchId } = useParams();
  const location = useLocation();
  const { selectedBranchId } = useBranch();
  const { hasPlanFeature } = usePlan();
  const attendanceOn = hasPlanFeature("attendanceManagement");

  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(null);

  // Re-fetch when the global branch changes (apiClient sends X-Branch-Id).
  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Batch status enum: 'Upcoming' | 'Ongoing' | 'Completed'.
      // Fetch all and exclude Completed so both Upcoming and Ongoing appear.
      const { data } = await apiClient.get("/batches/list?pageSize=200");
      setBatches((data.batches || []).filter((b) => b.status !== "Completed"));
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load batches.");
      setBatches([]);
    }
    setLoading(false);
  }, [selectedBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!batchId && attendanceOn) fetchBatches();
  }, [batchId, attendanceOn, fetchBatches]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return batches;
    return batches.filter(
      (b) => (b.name || "").toLowerCase().includes(q) || (b.course?.name || "").toLowerCase().includes(q)
    );
  }, [batches, search]);

  // ── Plan gate ──────────────────────────────────────────────────────────────
  if (!attendanceOn) {
    return (
      <div className="attendance-page sl-page">
        <PageHeader icon={BookOpen} title="Batch Register" description="Batch-wise attendance book" />
        <EmptyState
          icon={ClipboardCheck}
          title="Attendance module not enabled"
          description="The school/college attendance register is part of the Attendance Management add-on. Enable it on your plan to track batch-wise attendance."
        />
      </div>
    );
  }

  // ── Single batch → the register book ────────────────────────────────────────
  if (batchId) {
    return (
      <div className="attendance-page sl-page">
        <BatchAttendanceRegister
          batchId={batchId}
          defaultView="week"
          showHeader
          batchName={location.state?.batchName}
          onBack={() => navigate("/attendance/register")}
        />
      </div>
    );
  }

  // ── Grid of active batches ──────────────────────────────────────────────────
  return (
    <div className="attendance-page sl-page">
      <PageHeader
        icon={BookOpen}
        title="Batch Register"
        description="Pick a batch to open its attendance book — switch between week, month and 3-month views."
      />

      <div className="att-browser__search">
        <Search size={16} />
        <input
          type="text"
          placeholder="Search batches…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search batches"
        />
      </div>

      {loading ? (
        <div className="att-browser__grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="att-batchcard att-batchcard--ghost">
              <Shimmer height={96} count={1} />
            </div>
          ))}
        </div>
      ) : error ? (
        <EmptyState
          icon={Users}
          title="Could not load batches"
          description={error}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Users}
          title={search ? "No matching batches" : "No active batches"}
          description={search ? "Try a different search term." : "Create an active batch to start tracking attendance."}
        />
      ) : (
        <div className="att-browser__grid">
          {filtered.map((b) => (
            <button
              key={b._id}
              type="button"
              className="att-batchcard"
              onClick={() => navigate(`/attendance/register/${b._id}`, { state: { batchName: b.name } })}
            >
              <div className="att-batchcard__top">
                <span className="att-batchcard__name" title={b.name}>{b.name}</span>
                <ChevronRight size={16} className="att-batchcard__chev" />
              </div>
              <div className="att-batchcard__course">{b.course?.name || "—"}</div>
              <div className="att-batchcard__meta">
                <span><Users size={13} /> {b.studentCount ?? 0} student{b.studentCount === 1 ? "" : "s"}</span>
                {b.instructor && <span className="att-batchcard__instr"><GraduationCap size={13} /> {b.instructor}</span>}
              </div>
              {Array.isArray(b.meetingDays) && b.meetingDays.length > 0 ? (
                <div className="att-batchcard__days">
                  {b.meetingDays.slice().sort((x, y) => x - y).map((d) => (
                    <span key={d} className="att-batchcard__day">{DOW_SHORT[d]}</span>
                  ))}
                </div>
              ) : (
                <div className="att-batchcard__days att-batchcard__days--none">Schedule not set</div>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BatchAttendanceBrowserPage;
